"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PromptProvider = PromptProvider;
exports.usePrompt = usePrompt;
var _react = require("react");
var _reactRouterDom = require("react-router-dom");
var _jsxRuntime = require("react/jsx-runtime");
/**
* Nom : usePrompt.js
* Description : Ce fichier est un hook créé pour pour bloquer les navigations internes en React Router v6.
* Date de création : 31 aout 2026
*/

/* CSS et assets */
// None

/* Bibliothèques et composants */
// None

/* Hook */

var PromptContext = /*#__PURE__*/(0, _react.createContext)(null);
function PromptProvider(_ref) {
  var _window$history$state, _window$history$state2;
  var children = _ref.children;
  var navigate = (0, _reactRouterDom.useNavigate)();

  /**
   * Informations sur le blocage courant.
   *
   * On utilise une ref afin que le listener popstate
   * reste toujours installé, même lorsque TrialDetail
   * est démonté.
   */
  var promptRef = (0, _react.useRef)({
    shouldBlock: false,
    message: ""
  });

  /**
   * Index historique de la page actuellement affichée.
   */
  var currentIdxRef = (0, _react.useRef)((_window$history$state = (_window$history$state2 = window.history.state) === null || _window$history$state2 === void 0 ? void 0 : _window$history$state2.idx) !== null && _window$history$state !== void 0 ? _window$history$state : 0);

  /**
   * Indique que le prochain popstate est provoqué
   * par notre history.go() de restauration.
   */
  var restoringRef = (0, _react.useRef)(false);

  /**
   * Permet à TrialDetail d'activer/désactiver
   * la protection.
   */
  var setPrompt = (0, _react.useCallback)(function (shouldBlock, message) {
    promptRef.current = {
      shouldBlock: shouldBlock,
      message: message
    };
  }, []);

  /**
   * --------------------------------------------------
   * POPSTATE
   *
   * Ce listener est monté une seule fois au niveau
   * de RouterContent.
   * --------------------------------------------------
   */
  (0, _react.useEffect)(function () {
    var _window$history$state3;
    var handlePopState = function handlePopState(event) {
      var _event$state;
      /**
       * Notre propre history.go() utilisé pour
       * restaurer la page après un refus.
       */
      if (restoringRef.current) {
        restoringRef.current = false;
        console.log("[PromptProvider] restauration terminée");
        return;
      }
      var nextIdx = (_event$state = event.state) === null || _event$state === void 0 ? void 0 : _event$state.idx;
      console.log("[PromptProvider] POPSTATE", {
        currentIdx: currentIdxRef.current,
        nextIdx: nextIdx,
        shouldBlock: promptRef.current.shouldBlock,
        url: window.location.href
      });

      /**
       * Si ce n'est pas une entrée React Router,
       * on ne peut pas calculer le déplacement.
       */
      if (typeof nextIdx !== "number") {
        return;
      }
      var currentIdx = currentIdxRef.current;
      var delta = nextIdx - currentIdx;

      /**
       * Pas de déplacement.
       */
      if (delta === 0) {
        return;
      }

      /**
       * IMPORTANT :
       *
       * Même si le composant TrialDetail vient
       * d'être démonté par React Router, le
       * PromptProvider est toujours monté.
       */
      if (!promptRef.current.shouldBlock) {
        currentIdxRef.current = nextIdx;
        return;
      }
      var confirmed = window.confirm(promptRef.current.message);
      if (confirmed) {
        /**
         * Navigation autorisée.
         */
        currentIdxRef.current = nextIdx;
        return;
      }

      /**
       * Navigation refusée.
       *
       * BACK :
       *   delta = -1
       *   go(+1)
       *
       * FORWARD :
       *   delta = +1
       *   go(-1)
       */
      restoringRef.current = true;
      window.history.go(-delta);
    };
    window.addEventListener("popstate", handlePopState);

    /**
     * Synchronisation initiale.
     */
    var initialIdx = (_window$history$state3 = window.history.state) === null || _window$history$state3 === void 0 ? void 0 : _window$history$state3.idx;
    if (typeof initialIdx === "number") {
      currentIdxRef.current = initialIdx;
    }
    return function () {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  /**
   * --------------------------------------------------
   * NAVIGATION PROGRAMMATIQUE
   * --------------------------------------------------
   */
  var promptNavigate = (0, _react.useCallback)(function (to, options) {
    var _promptRef$current = promptRef.current,
      shouldBlock = _promptRef$current.shouldBlock,
      message = _promptRef$current.message;
    if (!shouldBlock) {
      navigate(to, options);
      return;
    }
    var confirmed = window.confirm(message);
    if (confirmed) {
      navigate(to, options);
    }
  }, [navigate]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(PromptContext.Provider, {
    value: {
      setPrompt: setPrompt,
      navigate: promptNavigate
    },
    children: children
  });
}

/**
 * Hook utilisé par TrialDetail.
 */
function usePrompt(message, shouldBlock) {
  var context = (0, _react.useContext)(PromptContext);
  if (!context) {
    throw new Error("usePrompt doit être utilisé à l'intérieur de PromptProvider");
  }
  var setPrompt = context.setPrompt,
    navigate = context.navigate;
  (0, _react.useEffect)(function () {
    setPrompt(shouldBlock, message);

    /**
     * IMPORTANT :
     * quand TrialDetail est démonté,
     * on désactive immédiatement la protection.
     */
    return function () {
      setPrompt(false, "");
    };
  }, [shouldBlock, message, setPrompt]);
  return navigate;
}